import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { TestScreen } from '../components/TestScreen';
import { ResultsScreen } from '../components/ResultsScreen';
import { generateCustomTest } from '../utils/testGenerator';

export const CustomTestPage: React.FC = () => {
  const { testId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setCurrentTest } = useApp();
  const [test, setTest] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeTest = async () => {
      try {
        // Try to get test from sessionStorage first
        const storedTest = sessionStorage.getItem(`test-${testId}`);
        if (storedTest) {
          const test = JSON.parse(storedTest);
          setTest(test);
          setCurrentTest && setCurrentTest(test);
          return;
        }
        
        if (location.state?.test) {
          // Test already generated and passed via navigation
          setTest(location.state.test);
          setCurrentTest && setCurrentTest(location.state.test);
        } else if (location.state?.config) {
          // Generate test from config
          const generatedTest = await generateCustomTest(location.state.config);
          setTest(generatedTest);
          setCurrentTest && setCurrentTest(generatedTest);
        } else {
          // No test data available
          navigate('/dashboard');
          return;
        }
      } catch (error) {
        console.error('Error initializing test:', error);
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    initializeTest();
  }, [testId, location.state, navigate, setCurrentTest]);

  const handleSubmitTest = (testResults) => {
    setResults(testResults);
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white text-black dark:bg-gray-800 dark:text-white">
        <div className="w-16 h-16 border-4 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="flex items-center justify-center h-screen bg-white text-black dark:bg-gray-800 dark:text-white">
        <div className="text-center">
          <h2 className="text-xl font-bold text-brand-text-primary mb-2">Test Not Found</h2>
          <p className="text-brand-text-secondary mb-4">The test you're looking for could not be loaded.</p>
          <button 
            onClick={handleBackToDashboard}
            className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (results) {
    return (
      <div className="bg-white text-black dark:bg-gray-800 dark:text-white">
        <ResultsScreen
          results={results}
          test={test}
          user={user}
          onBack={handleBackToDashboard}
          onViewDetails={() => {
            // Navigate to detailed results if needed
            navigate(`/test/${testId}/detailed-results`, { 
              state: { results, test, questions: test.questions } 
            });
          }}
        />
      </div>
    );
  }

  return (
    <div className="bg-white text-black dark:bg-gray-800 dark:text-white">
      <TestScreen
        test={test}
        user={user}
        onSubmitTest={handleSubmitTest}
        onBack={handleBackToDashboard}
      />
    </div>
  );
};